import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, Loader } from "@/components/common";
import { TextField, SelectField, AreaField } from "@/components/fields";
import { money, PROPERTY_TYPES, PROPERTY_CATEGORIES, PROPERTY_STATUS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { House, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const FEATURE_NUMS = ["bedrooms", "bathrooms", "lounges", "kitchens", "parking"];
const FEATURE_BOOLS = ["Servant Quarter", "Swimming Pool", "Gym", "Sauna", "Steam Room", "Garden",
  "Balcony", "Lift", "CCTV", "Backup Generator", "Borehole", "Internet"];

const empty = {
  name: "", property_type: "Apartment", category: "Residential", status: "Vacant",
  unit_no: "", lr_no: "", country: "Kenya", county: "", city: "", estate: "",
  physical_address: "", gps: "", landlord_id: "", sourcing_staff_id: "", market_rent: 0,
  management_fee_pct: "", image_url: "", features: {},
};

export default function Properties() {
  const { user, company } = useAuth();
  const cur = company?.currency || "KES";
  const [items, setItems] = useState(null);
  const [landlords, setLandlords] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("all");

  const canEdit = ["property_manager", "property_admin", "staff"].includes(user.role);

  const load = () => {
    api.get("/properties").then((r) => setItems(r.data));
    api.get("/landlords").then((r) => setLandlords(r.data)).catch(() => {});
    api.get("/users").then((r) => setStaff(r.data.filter((u) => u.role === "staff"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFeat = (k, v) => setForm((f) => ({ ...f, features: { ...f.features, [k]: v } }));

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p, features: p.features || {} }); setEditId(p.id); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Property name is required");
    try {
      const payload = { ...form, landlord_id: form.landlord_id || null, sourcing_staff_id: form.sourcing_staff_id || null,
        management_fee_pct: form.management_fee_pct === "" || form.management_fee_pct === null ? null : Number(form.management_fee_pct) };
      if (editId) await api.put(`/properties/${editId}`, payload);
      else await api.post("/properties", payload);
      toast.success(editId ? "Property updated" : "Property registered");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    await api.delete(`/properties/${id}`); toast.success("Deleted"); load();
  };

  if (!items) return <Loader />;
  const filtered = filter === "all" ? items : items.filter((p) => p.status === filter);

  return (
    <div>
      <PageHeader title="Properties" subtitle="Your managed property portfolio" icon={House}
        actions={canEdit && <Button onClick={openNew} data-testid="add-property-btn" className="gap-2"><Plus size={16} /> Register Property</Button>} />

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="filter-all">All ({items.length})</TabsTrigger>
          {PROPERTY_STATUS.map((s) => <TabsTrigger key={s} value={s}>{s}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {filtered.length === 0 ? (
        <EmptyState icon={House} title="No properties yet" message="Register your first property to start managing it."
          action={canEdit && <Button onClick={openNew}><Plus size={16} className="mr-2" />Register Property</Button>} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property</TableHead><TableHead>Type</TableHead><TableHead>Location</TableHead>
                  <TableHead>Status</TableHead><TableHead className="text-right">Market Rent</TableHead>
                  {canEdit && <TableHead></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id} data-testid={`property-row-${p.id}`}>
                    <TableCell>
                      <div className="font-medium">{p.name} {p.unit_no && <span className="text-muted-foreground">· {p.unit_no}</span>}</div>
                      <div className="text-xs text-muted-foreground">{p.category}</div>
                    </TableCell>
                    <TableCell>{p.property_type}</TableCell>
                    <TableCell className="text-sm">{[p.estate, p.city, p.county].filter(Boolean).join(", ") || "—"}</TableCell>
                    <TableCell><StatusBadge status={p.status} /></TableCell>
                    <TableCell className="text-right font-mono">{money(p.market_rent, cur)}</TableCell>
                    {canEdit && (
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}><PencilSimple size={16} /></Button>
                          {user.role !== "staff" && <Button size="icon" variant="ghost" onClick={() => del(p.id)} data-testid={`delete-${p.id}`}><Trash size={16} className="text-destructive" /></Button>}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Property" : "Register Property"}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Property Name" value={form.name} onChange={(v) => set("name", v)} required testId="prop-name" />
              <TextField label="Unit No." value={form.unit_no} onChange={(v) => set("unit_no", v)} testId="prop-unit" />
              <SelectField label="Type" value={form.property_type} onChange={(v) => set("property_type", v)} options={PROPERTY_TYPES} testId="prop-type" />
              <SelectField label="Category" value={form.category} onChange={(v) => set("category", v)} options={PROPERTY_CATEGORIES} />
              <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} options={PROPERTY_STATUS} />
              <TextField label="LR No." value={form.lr_no} onChange={(v) => set("lr_no", v)} />
              <TextField label="Market Rent" type="number" value={form.market_rent} onChange={(v) => set("market_rent", v)} testId="prop-rent" />
              <TextField label="Mgmt Fee % (optional)" type="number" value={form.management_fee_pct} onChange={(v) => set("management_fee_pct", v)} testId="prop-mgmt-fee" />
              <TextField label="Image URL" value={form.image_url} onChange={(v) => set("image_url", v)} />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <TextField label="Country" value={form.country} onChange={(v) => set("country", v)} />
              <TextField label="County/State" value={form.county} onChange={(v) => set("county", v)} />
              <TextField label="City/Town" value={form.city} onChange={(v) => set("city", v)} />
              <TextField label="Estate/Area" value={form.estate} onChange={(v) => set("estate", v)} />
              <TextField label="GPS Coordinates" value={form.gps} onChange={(v) => set("gps", v)} />
            </div>
            <AreaField label="Physical Address" value={form.physical_address} onChange={(v) => set("physical_address", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <SelectField label="Landlord" value={form.landlord_id} onChange={(v) => set("landlord_id", v)}
                options={[{ value: "", label: "— None —" }, ...landlords.map((l) => ({ value: l.id, label: l.name }))]} />
              <SelectField label="Sourcing Staff" value={form.sourcing_staff_id} onChange={(v) => set("sourcing_staff_id", v)}
                options={[{ value: "", label: "— None —" }, ...staff.map((s) => ({ value: s.id, label: s.name }))]} testId="prop-sourcing" />
            </div>
            <div>
              <div className="text-sm font-medium mb-2">Features</div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
                {FEATURE_NUMS.map((k) => (
                  <TextField key={k} label={k[0].toUpperCase() + k.slice(1)} type="number"
                    value={form.features[k] || 0} onChange={(v) => setFeat(k, v)} />
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {FEATURE_BOOLS.map((k) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={!!form.features[k]} onCheckedChange={(v) => setFeat(k, !!v)} /> {k}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-property-btn">{editId ? "Save" : "Register"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
